import {BUNNY_STATUS, isAbandoned} from '@/utils/bunny-status';

// The six states the upload area can be in. Derived entirely from
// (isUploading, topic) -- this module holds no state of its own.
export const STAGE = Object.freeze({
  EMPTY: 'EMPTY',
  UPLOADING: 'UPLOADING',
  PROCESSING: 'PROCESSING',
  READY: 'READY',
  FAILED: 'FAILED',
  ABANDONED: 'ABANDONED',
});

const FAILURE_STATUSES = [BUNNY_STATUS.ERROR, BUNNY_STATUS.UPLOAD_FAILED];

// step/state feed a three-circle stepper: circle i renders as done when
// i < step, as `state` when i === step, and as upcoming when i > step.
// EMPTY uses step 0 so no circle is lit at all.
const TABLE = {
  [STAGE.EMPTY]: {
    step: 0, state: 'idle',
    canDelete: false, canDownload: false, canCancel: false, canRefresh: false,
    showDropzone: true,
  },
  [STAGE.UPLOADING]: {
    step: 1, state: 'active',
    canDelete: false, canDownload: false, canCancel: true, canRefresh: false,
    showDropzone: false,
  },
  [STAGE.PROCESSING]: {
    step: 2, state: 'active',
    // canCancel is the escape hatch for a transfer that died before Bunny
    // ever received any bytes: the topic keeps its guid with bunnyStatus
    // null, which reads as PROCESSING forever since isAbandoned only fires
    // once bunnyStatusAt is >24h old, and a Refresh in the meantime just
    // stores Bunny's real "0 Created" status -- a non-null status, so
    // isAbandoned can then never fire and the topic is wedged permanently.
    // Cancel deletes the guid outright and is safe to offer here regardless
    // of how far encoding has actually gotten, unlike canDelete (left
    // false): Delete mid-encode remains deliberately unavailable.
    canDelete: false, canDownload: false, canCancel: true, canRefresh: true,
    showDropzone: false,
  },
  [STAGE.READY]: {
    step: 3, state: 'done',
    canDelete: true, canDownload: true, canCancel: false, canRefresh: false,
    showDropzone: true,
  },
  [STAGE.FAILED]: {
    step: 2, state: 'error',
    canDelete: true, canDownload: false, canCancel: false, canRefresh: false,
    showDropzone: true,
  },
  [STAGE.ABANDONED]: {
    step: 1, state: 'error',
    canDelete: false, canDownload: false, canCancel: true, canRefresh: true,
    showDropzone: true,
  },
};

/**
 * Resolve which upload stage a topic is in.
 *
 * Evaluation order is part of the contract:
 *   1. isUploading, because bunnyStatus stays null for the whole transfer
 *      and there may be no guid yet while the ticket request is in flight.
 *   2. no guid at all -> nothing has ever been uploaded.
 *   3. abandonment, which is a null-status condition and would otherwise be
 *      swallowed by the PROCESSING fallthrough.
 *   4. the status number.
 *
 * @param {{isUploading: boolean, topic: object|null, now: number|undefined}} args
 *   `now` is epoch ms and optional; it exists so a caller that recomputes on a
 *   clock tick can pass the same instant it is rendering with. Step 3 below is
 *   the only time-dependent branch, and left to read the real clock itself it
 *   makes this function silently non-deterministic for a Vue computed.
 * @return {{stage: string, step: number, state: string, canDelete: boolean,
 *   canDownload: boolean, canCancel: boolean, canRefresh: boolean,
 *   showDropzone: boolean}}
 */
export function uploadStage({isUploading, topic, now}) {
  const stage = resolveStage(isUploading, topic, now);
  return Object.assign({stage}, TABLE[stage]);
}

/**
 * @param {boolean} isUploading
 * @param {object|null} topic
 * @param {number|undefined} now
 * @return {string} a STAGE value
 */
function resolveStage(isUploading, topic, now) {
  if (isUploading) return STAGE.UPLOADING;

  const guid = topic && topic.bunnyVideoGuid;
  if (!guid) return STAGE.EMPTY;

  const status = topic.bunnyStatus;
  if (isAbandoned(status, topic.bunnyStatusAt, now)) return STAGE.ABANDONED;
  if (status === BUNNY_STATUS.FINISHED) return STAGE.READY;
  if (FAILURE_STATUSES.indexOf(status) !== -1) return STAGE.FAILED;

  // Everything else -- null, 0, 1, 2, 3, 7, 8, and any code Bunny adds
  // later. The fallthrough is deliberately the safe one: it never claims a
  // video is ready and it always leaves Refresh reachable.
  return STAGE.PROCESSING;
}

/**
 * Whether the screen should keep asking Bunny for this topic's status.
 *
 * True for PROCESSING and nothing else, and derived from uploadStage rather
 * than restated as its own condition so the two can never disagree:
 * UPLOADING is tus-js-client's to report, READY and FAILED are terminal,
 * ABANDONED is wedged by definition (polling cannot move it -- Cancel and
 * Refresh are what that stage offers), and EMPTY has no video to ask about.
 *
 * @param {{isUploading: boolean, topic: object|null, now: number|undefined}} args
 *   the same arguments uploadStage takes, `now` included: a caller rendering
 *   against a ticking clock must be able to decide on the same instant it is
 *   drawing with.
 * @return {boolean}
 */
export function shouldAutoPoll(args) {
  return uploadStage(args).stage === STAGE.PROCESSING;
}
